import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Search, MapPin, User, Building, Phone, Mail, Clock, X, Filter, ArrowLeft } from 'lucide-react';
import { UserHeader } from '../components/UserHeader';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MUNICIPIOS = [
  "Lomas de Zamora", "Lanús", "Avellaneda", "Almirante Brown", 
  "Esteban Echeverría", "Ezeiza", "Presidente Perón", "Quilmes", 
  "CABA", "La Plata", "San Vicente", "Cañuelas", "Otro"
];

const AREAS_TRABAJO = [
  "Salud", "Desarrollo Social", "Educación", "Discapacidad",
  "Hábitat y Ambiente", "Niñez y Adolescencia", "Géneros y Diversidad",
  "Justicia", "Serv. Penitenciario", "Patronato Liberados", "Hábitat", "Investigación"
];

const TIPOS_INSTITUCION = [
  "Administración Pública Nacional",
  "Administración Pública Provincial",
  "Administración Pública Municipal",
  "Organización de la Sociedad Civil (ONG)",
  "Ámbito privado"
];

const AREA_COLORS = {
  "Salud": "bg-red-100 text-red-700",
  "Desarrollo Social": "bg-blue-100 text-blue-700",
  "Educación": "bg-yellow-100 text-yellow-700",
  "Discapacidad": "bg-purple-100 text-purple-700",
  "Hábitat y Ambiente": "bg-green-100 text-green-700",
  "Niñez y Adolescencia": "bg-pink-100 text-pink-700",
  "Géneros y Diversidad": "bg-violet-100 text-violet-700",
  "Justicia": "bg-indigo-100 text-indigo-700",
  "Serv. Penitenciario": "bg-gray-100 text-gray-700",
  "Patronato Liberados": "bg-orange-100 text-orange-700",
  "Hábitat": "bg-teal-100 text-teal-700",
  "Investigación": "bg-cyan-100 text-cyan-700"
};

export default function RecurseroColegasPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [busqueda, setBusqueda] = useState('');

  const [filtros, setFiltros] = useState({
    municipio: searchParams.get('municipio') || '',
    area_trabajo: searchParams.get('area') || '',
    tipo_institucion: searchParams.get('tipo') || ''
  });

  // Verificación de autenticación
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      // Guardar redirect para volver al recursero después del login
      sessionStorage.setItem('redirectAfterLogin', '/recursero');
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      buscarRecursero();
    }
  }, [isAuthenticated]);

  // Loading mientras verifica auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const buscarRecursero = async (filters = filtros, searchText = busqueda) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/recursero/buscar`, {
        municipio: filters.municipio || null,
        area_trabajo: filters.area_trabajo || null,
        tipo_institucion: filters.tipo_institucion || null,
        busqueda: searchText || null
      });
      setResults(response.data);
    } catch (err) {
      console.error('Error buscando recursero:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setBusqueda(value);
    // Debounce search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      buscarRecursero(filtros, value);
    }, 300);
  };

  const handleFilterChange = (key, value) => {
    const newFiltros = { ...filtros, [key]: value };
    setFiltros(newFiltros);
    buscarRecursero(newFiltros);
  };

  const clearFilters = () => {
    const emptyFiltros = { municipio: '', area_trabajo: '', tipo_institucion: '' };
    setFiltros(emptyFiltros);
    setBusqueda('');
    buscarRecursero(emptyFiltros, '');
  };

  const hasFilters = filtros.municipio || filtros.area_trabajo || filtros.tipo_institucion || busqueda;

  // Detail view
  if (selectedItem) {
    return (
      <div className="min-h-screen bg-gray-50">
        <UserHeader />
        
        {/* Subtítulo con volver */}
        <div className="bg-white border-b">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <Button 
              variant="ghost" 
              onClick={() => setSelectedItem(null)}
              data-testid="back-to-recursero-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver al Recursero
            </Button>
          </div>
        </div>

        <main className="max-w-4xl mx-auto px-4 py-8 animate-fade-in">
          <Card>
            <CardContent className="pt-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-6">Información del Profesional</h1>

              {/* Profesional Info */}
              <div className="flex items-start gap-4 mb-8 pb-6 border-b border-gray-200">
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
                  <User className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedItem.nombre_completo}</h2>
                  <p className="text-gray-600">{selectedItem.titulo}</p>
                  <p className="text-sm text-gray-500">Matrícula: {selectedItem.numero_matricula}</p>
                  
                  {/* Contact Info */}
                  <div className="mt-3 space-y-1">
                    {selectedItem.celular && (
                      <div className="text-sm">
                        <p className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4" />
                          {selectedItem.celular}
                        </p>
                        <p className="text-xs text-gray-400 ml-6 italic">
                          (Tel. personal – Por favor, tené en cuenta días y horarios al comunicarte)
                        </p>
                      </div>
                    )}
                    {selectedItem.email && (
                      <p className="text-sm flex items-center gap-2 text-gray-600">
                        <Mail className="w-4 h-4" />
                        {selectedItem.email}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Trabajo Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Información del Trabajo</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedItem.tipo_institucion && (
                    <div>
                      <p className="text-sm text-gray-500">Tipo de Institución</p>
                      <p className="font-medium">{selectedItem.tipo_institucion}</p>
                    </div>
                  )}
                  {selectedItem.nombre_institucion && (
                    <div>
                      <p className="text-sm text-gray-500">Organismo</p>
                      <p className="font-medium">{selectedItem.nombre_institucion}</p>
                    </div>
                  )}
                  {selectedItem.nombre_area && (
                    <div>
                      <p className="text-sm text-gray-500">Área</p>
                      <p className="font-medium">{selectedItem.nombre_area}</p>
                    </div>
                  )}
                  {selectedItem.municipio && (
                    <div>
                      <p className="text-sm text-gray-500">Municipio</p>
                      <p className="font-medium flex items-center gap-1">
                        <MapPin className="w-4 h-4 text-primary" />
                        {selectedItem.municipio}
                      </p>
                    </div>
                  )}
                </div>

                {selectedItem.domicilio_institucion && (
                  <div>
                    <p className="text-sm text-gray-500">Domicilio</p>
                    <p className="font-medium">{selectedItem.domicilio_institucion}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedItem.telefono_institucion && (
                    <div>
                      <p className="text-sm text-gray-500">Teléfono Institución</p>
                      <p className="font-medium flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {selectedItem.telefono_institucion}
                      </p>
                    </div>
                  )}
                  {selectedItem.email_institucion && (
                    <div>
                      <p className="text-sm text-gray-500">Email Institución</p>
                      <p className="font-medium flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {selectedItem.email_institucion}
                      </p>
                    </div>
                  )}
                </div>

                {selectedItem.horarios_atencion && (
                  <div>
                    <p className="text-sm text-gray-500">Horarios de Atención</p>
                    <p className="font-medium flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {selectedItem.horarios_atencion}
                    </p>
                  </div>
                )}

                {selectedItem.responsable_nombre && (
                  <div>
                    <p className="text-sm text-gray-500">Responsable</p>
                    <p className="font-medium">{selectedItem.responsable_nombre}</p>
                  </div>
                )}

                {selectedItem.areas_trabajo?.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Áreas de Trabajo</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedItem.areas_trabajo.map(area => (
                        <Badge key={area} className={AREA_COLORS[area] || "bg-gray-100 text-gray-700"}>
                          {area}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selectedItem.demandas_respuesta && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-700 font-medium mb-1">Demandas que atiende</p>
                    <p className="text-blue-800">{selectedItem.demandas_respuesta}</p>
                  </div>
                )}

                {selectedItem.comentarios_indicaciones && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-700 font-medium mb-1">Indicaciones</p>
                    <p className="text-amber-800">{selectedItem.comentarios_indicaciones}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Skip link para accesibilidad */}
      <a href="#resultados-recursero" className="skip-link">
        Ir a los resultados del recursero
      </a>
      
      {/* Header unificado */}
      <UserHeader />
      
      {/* Subtítulo de sección */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Recursero de Colegas</h1>
          <p className="text-gray-500">
            Encontrá trabajadorxs sociales por territorio y área de trabajo
          </p>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-8" role="main">
        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5 text-gray-500" aria-hidden="true" />
              <h2 className="font-semibold text-gray-900" id="filtros-title">Filtros</h2>
            </div>
            
            {/* Buscador por nombre */}
            <div className="mb-4">
              <label htmlFor="search-nombre" className="sr-only">Buscar por nombre o apellido</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
                <Input
                  id="search-nombre"
                  type="text"
                  placeholder="Buscar por nombre o apellido..."
                  value={busqueda}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10"
                  data-testid="search-nombre"
                  aria-label="Buscar por nombre o apellido"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" role="search" aria-labelledby="filtros-title">
              <div>
                <label htmlFor="filter-municipio" className="sr-only">Filtrar por municipio</label>
                <Select value={filtros.municipio} onValueChange={(v) => handleFilterChange('municipio', v)}>
                  <SelectTrigger data-testid="filter-municipio" id="filter-municipio" aria-label="Seleccionar municipio">
                    <SelectValue placeholder="Municipio/Territorio" />
                  </SelectTrigger>
                  <SelectContent>
                    {MUNICIPIOS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label htmlFor="filter-area" className="sr-only">Filtrar por área de trabajo</label>
                <Select value={filtros.area_trabajo} onValueChange={(v) => handleFilterChange('area_trabajo', v)}>
                  <SelectTrigger data-testid="filter-area" id="filter-area" aria-label="Seleccionar área de trabajo">
                    <SelectValue placeholder="Área de Trabajo" />
                  </SelectTrigger>
                  <SelectContent>
                    {AREAS_TRABAJO.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label htmlFor="filter-tipo" className="sr-only">Filtrar por tipo de institución</label>
                <Select value={filtros.tipo_institucion} onValueChange={(v) => handleFilterChange('tipo_institucion', v)}>
                  <SelectTrigger data-testid="filter-tipo" id="filter-tipo" aria-label="Seleccionar tipo de institución">
                  <SelectValue placeholder="Tipo de Institución" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_INSTITUCION.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            </div>
            
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="mt-4 text-sm text-primary hover:underline flex items-center gap-1"
                data-testid="clear-filters-btn"
                aria-label="Limpiar todos los filtros"
              >
                <X className="w-4 h-4" aria-hidden="true" />
                Limpiar filtros
              </button>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        <section id="resultados-recursero" aria-label="Resultados del recursero">
          {loading ? (
            <div className="text-center py-12" role="status" aria-label="Cargando resultados">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" aria-hidden="true"></div>
              <p className="mt-4 text-gray-500">Cargando recursos...</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-12" role="status">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" aria-hidden="true" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No se encontraron resultados</h3>
              <p className="text-gray-500">Probá ajustando los filtros de búsqueda</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4" aria-live="polite">{results.length} resultado(s) encontrado(s)</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" role="list" aria-label="Lista de profesionales">
                {results.map((item, index) => (
                  <Card 
                    key={`${item.trabajador_id}-${item.trabajo_id}-${index}`} 
                    className="card-hover cursor-pointer"
                    onClick={() => setSelectedItem(item)}
                    onKeyDown={(e) => e.key === 'Enter' && setSelectedItem(item)}
                    data-testid={`recursero-card-${index}`}
                    role="listitem"
                    tabIndex={0}
                    aria-label={`Ver información de ${item.nombre_completo}, ${item.nombre_institucion}`}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0" aria-hidden="true">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-gray-900 truncate">{item.nombre_completo}</h3>
                          <p className="text-sm text-gray-600 truncate">
                            {item.nombre_institucion} {item.nombre_area ? `- ${item.nombre_area}` : ''}
                          </p>
                        </div>
                      </div>

                      <p className="text-sm text-gray-500 flex items-center gap-1 mb-3">
                        <MapPin className="w-4 h-4 text-primary" aria-hidden="true" />
                        <span>{item.municipio}</span>
                      </p>

                      <div className="flex flex-wrap gap-1 mb-4" aria-label="Áreas de trabajo">
                        {item.areas_trabajo.slice(0, 3).map(area => (
                          <Badge key={area} variant="secondary" className={`text-xs ${AREA_COLORS[area] || ''}`}>
                            {area}
                        </Badge>
                      ))}
                      {item.areas_trabajo.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{item.areas_trabajo.length - 3}
                        </Badge>
                      )}
                    </div>

                    <button className="text-sm text-primary hover:underline font-medium" aria-hidden="true">
                      Ver información completa →
                    </button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Botón volver */}
        <div className="text-center mt-8 pb-8">
          <Button 
            variant="outline" 
            onClick={() => navigate('/recursero/seleccionar')}
            data-testid="back-to-recursero-select-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a selección
          </Button>
        </div>
        </section>
      </main>
    </div>
  );
}
